import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { Select } from '@/_components';
import { SelectorWithAvatarContainer } from '@/_containers';
import { useGetCompetitorsMutation } from '@/_pages/competitors/shared';
import type { CompetitorQueryLinkerSchema } from '@/_pages/competitors/shared/schemas/competitor-query-linker-schema';
import type { BrandQueryLinkerSchema } from '@/_pages/settings/brands/shared';
import { useGetBrandsMutation } from '@/_pages/settings/brands/shared';
import { nameResolver, useBoolean, useDebounce } from '@/_shared';

import { useCompanyFormContext } from '../../form-context';
import type { CompanyWebProviderTypeSchema } from '../../schemas/company-query-schema';

type WebProviderComboboxProps = {
  disabled?: boolean;
  className?: string;
};

type WebProviderOptionType = CompetitorQueryLinkerSchema | BrandQueryLinkerSchema;

type WebProviderGroupedOption = {
  label: string;
  options: WebProviderOptionType[];
};

export function WebProviderCombobox({ disabled = false, className }: WebProviderComboboxProps) {
  const form = useCompanyFormContext();
  const [current_web_provider] = form.watch(['current_web_provider']);

  const [isFocused, focus] = useBoolean();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const fetchDefaultOptionsRef = useRef(true);

  const {
    mutateAsync: getBrandsOptions,
    data: brandsData,
    isPending: isLoadingBrands,
  } = useGetBrandsMutation({
    onSuccess: () => {
      focus.setFalse();
    },
  });
  const {
    mutateAsync: getCompetitorsOptions,
    data: competitorsData,
    isPending: isLoadingCompetitors,
  } = useGetCompetitorsMutation({
    onSuccess: () => {
      focus.setFalse();
    },
  });

  const formatGroupLabel = useCallback((data: WebProviderGroupedOption) => <div>{data.label}</div>, []);
  const getOptionLabel = useCallback((option: WebProviderOptionType) => option?.name, []);
  const getOptionValue = useCallback((option: WebProviderOptionType) => option?.id.toString(), []);

  const fetchOptions = useCallback(
    (search = '', per_page = 100) => {
      if ((!isFocused || disabled) && !search) {
        return;
      }
      getBrandsOptions({ search, per_page });
      getCompetitorsOptions({ search, per_page });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, isFocused]
  );

  const updateValue = useCallback(
    (val: BrandQueryLinkerSchema | CompetitorQueryLinkerSchema | null, type: CompanyWebProviderTypeSchema | null) => {
      form.setValue('current_web_provider', val, { shouldDirty: true });
      form.setValue('current_web_provider_id', val ? val.id : null, { shouldDirty: true });
      form.setValue('current_web_provider_type', type, { shouldDirty: true });
    },
    [form]
  );

  const handleChange = useCallback(
    (v: WebProviderOptionType | null) => {
      if (!v) {
        updateValue(null, null);
      } else if (isBrand(v)) {
        updateValue(v, 'brand');
      } else if (isCompetitor(v)) {
        updateValue(v, 'competitor');
      }
    },
    [updateValue]
  );

  const getLogo = useCallback(
    (provider: BrandQueryLinkerSchema | CompetitorQueryLinkerSchema | null | undefined): string => {
      let src: string | undefined = '';
      if (provider) {
        if (isBrand(provider)) {
          src = provider?.icon?.at(0)?.url;
        } else if (isCompetitor(provider)) {
          src = provider?.logo?.at(0)?.url;
        }
      }
      return src ?? '';
    },
    []
  );

  useEffect(() => {
    if (fetchDefaultOptionsRef.current) {
      fetchOptions();
      fetchDefaultOptionsRef.current = false;
    }
  }, [fetchOptions]);

  useEffect(() => {
    if (!fetchDefaultOptionsRef.current) {
      fetchOptions(debouncedQuery);
    }
  }, [fetchOptions, debouncedQuery]);

  const groupedOptions = useMemo(
    (): WebProviderGroupedOption[] => [
      {
        label: 'Brands',
        options: brandsData || [],
      },
      {
        label: 'Competitors',
        options: competitorsData || [],
      },
    ],
    [brandsData, competitorsData]
  );

  return (
    <SelectorWithAvatarContainer
      disabled={disabled}
      src={getLogo(current_web_provider)}
      label={nameResolver(current_web_provider?.name, undefined, 'Current Web Provider')}
      name={current_web_provider?.name ?? ''}
      className={twMerge(className, 'lg:flex-row')}
      avatarClassName={current_web_provider && !isBrand(current_web_provider) ? 'w-14 rounded-md' : ''}
    >
      <Select<WebProviderOptionType, false, WebProviderGroupedOption>
        label="Current Web Provider"
        value={current_web_provider}
        options={groupedOptions}
        formatGroupLabel={formatGroupLabel}
        onInputChange={setQuery}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        onChange={handleChange}
        onFocus={focus.setTrue}
        onBlur={focus.setFalse}
        isLoading={isLoadingBrands || isLoadingCompetitors}
        disabled={disabled}
      />
    </SelectorWithAvatarContainer>
  );
}

function isCompetitor(option: WebProviderOptionType): option is CompetitorQueryLinkerSchema {
  return 'logo' in option;
}

function isBrand(option: WebProviderOptionType): option is BrandQueryLinkerSchema {
  return 'legal_name' in option;
}
